import type { ActiveCall, CallStatus, IncomingCall } from '../store/callStore';

export type CallStateSnapshot = {
  activeCall: ActiveCall | null;
  incomingCall: IncomingCall | null;
  connectionState: RTCPeerConnectionState | null;
  isCallMinimized: boolean;
};

export type CallStateAction =
  | {
      type: 'set-active-call';
      call: ActiveCall | null;
    }
  | {
      type: 'call/active-updated';
      patch: Partial<ActiveCall>;
    }
  | {
      type: 'set-incoming-call';
      call: IncomingCall | null;
    }
  | {
      type: 'call/incoming-received';
      call: IncomingCall;
    }
  | {
      type: 'set-connection-state';
      state: RTCPeerConnectionState | null;
    }
  | {
      type: 'update-call-status';
      status: CallStatus;
    }
  | {
      type: 'end-call';
    }
  | {
      type: 'call/outgoing-started';
      call: ActiveCall;
    }
  | {
      type: 'call/outgoing-ringing';
    }
  | {
      type: 'call/incoming-answer-started';
      call: ActiveCall;
    }
  | {
      type: 'call/incoming-answer-failed';
      incomingCall: IncomingCall;
    }
  | {
      type: 'call/native-event-applied';
      call: ActiveCall;
    }
  | {
      type: 'call/native-upgrade-requested';
      call: ActiveCall;
    }
  | {
      type: 'call/security-verified';
      call: ActiveCall;
    };

function normalizeActiveCall(call: ActiveCall | null): ActiveCall | null {
  if (!call) return null;

  if (call.status === 'active') {
    return { ...call, connectionPhase: undefined };
  }

  if (call.status === 'ringing') {
    return { ...call, connectionPhase: 'ringing' };
  }

  if (call.status === 'initiating') {
    return { ...call, connectionPhase: 'preparing' };
  }

  if (
    call.status === 'ending'
    || call.status === 'handoff'
    || call.status === 'idle'
  ) {
    return { ...call, connectionPhase: undefined };
  }

  if (
    call.status === 'connecting'
    && (call.connectionPhase === 'preparing' || call.connectionPhase === 'ringing')
  ) {
    return { ...call, connectionPhase: 'answer_received' };
  }

  return call;
}

export function reduceCallState(state: CallStateSnapshot, action: CallStateAction): CallStateSnapshot {
  switch (action.type) {
    case 'set-active-call': {
      const nextSetActiveCall = normalizeActiveCall(action.call);
      return {
        ...state,
        activeCall: nextSetActiveCall,
        incomingCall: nextSetActiveCall && state.incomingCall?.callSessionId === nextSetActiveCall.callSessionId
          ? null
          : state.incomingCall,
        isCallMinimized: nextSetActiveCall && state.activeCall?.callSessionId === nextSetActiveCall.callSessionId
          ? state.isCallMinimized
          : false
      };
    }
    case 'call/active-updated':
      return {
        ...state,
        activeCall: state.activeCall
          ? normalizeActiveCall({ ...state.activeCall, ...action.patch })
          : null
      };
    case 'set-incoming-call':
      return {
        ...state,
        incomingCall: action.call
      };
    case 'call/incoming-received':
      return {
        ...state,
        incomingCall: action.call
      };
    case 'set-connection-state':
      return {
        ...state,
        connectionState: action.state
      };
    case 'update-call-status':
      return {
        ...state,
        activeCall: state.activeCall ? normalizeActiveCall({ ...state.activeCall, status: action.status }) : null
      };
    case 'end-call':
      return {
        ...state,
        activeCall: null,
        incomingCall: null,
        connectionState: null,
        isCallMinimized: false
      };
    case 'call/outgoing-started': {
      const nextOutgoingCall = normalizeActiveCall(action.call);
      return {
        ...state,
        activeCall: nextOutgoingCall,
        incomingCall: nextOutgoingCall && state.incomingCall?.callSessionId === nextOutgoingCall.callSessionId
          ? null
          : state.incomingCall,
        isCallMinimized: false
      };
    }
    case 'call/outgoing-ringing':
      return {
        ...state,
        activeCall: state.activeCall
          ? { ...state.activeCall, status: 'ringing', connectionPhase: 'ringing' }
          : null
      };
    case 'call/incoming-answer-started': {
      const nextIncomingAnswerCall = normalizeActiveCall(action.call);
      return {
        ...state,
        activeCall: nextIncomingAnswerCall,
        incomingCall: null,
        isCallMinimized: false
      };
    }
    case 'call/incoming-answer-failed':
      return {
        ...state,
        activeCall: null,
        incomingCall: action.incomingCall,
        isCallMinimized: false
      };
    case 'call/native-event-applied':
    case 'call/native-upgrade-requested':
    case 'call/security-verified': {
      const nextNativeCall = normalizeActiveCall(action.call);
      return {
        ...state,
        activeCall: nextNativeCall,
        incomingCall: nextNativeCall && state.incomingCall?.callSessionId === nextNativeCall.callSessionId
          ? null
          : state.incomingCall,
        isCallMinimized: nextNativeCall && nextNativeCall.callSessionId === state.activeCall?.callSessionId
          ? state.isCallMinimized
          : false
      };
    }
    default:
      return state;
  }
}
